import React from 'react';
import { WidgetProps } from '@rjsf/utils';
import { Typography, makeStyles, createStyles, Link } from '@bb-ui/react-library';
import { useTranslation } from 'react-i18next';
import { HierarchyTenant } from 'App.types';
import { useDataSharingContext } from 'contexts/DataSharingContext';
import { isLearnOrUnsetType } from 'utils/tenantType';

const useStyles = makeStyles((theme) =>
  createStyles({
    container: {
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(2),
    },
    label: {
      marginBottom: theme.spacing(1.5),
      fontSize: '0.875rem',
      fontWeight: 600,
    },
    requiredAsterisk: {
      color: theme.palette.error.main,
    },
    table: {
      width: '100%',
      border: `1px solid ${theme.palette.divider}`,
      borderCollapse: 'collapse',
      borderRadius: theme.shape.borderRadius,
    },
    tableHeaderRow: {
      backgroundColor: theme.palette.background.default,
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    tableCell: {
      padding: theme.spacing(0.5, 1.5),
      textAlign: 'center',
    },
    tableCellLeft: {
      padding: theme.spacing(0.5, 1.5),
      textAlign: 'left',
      wordBreak: 'break-all',
    },
    tableCellType: {
      padding: theme.spacing(0.5, 0),
      textAlign: 'center',
    },
    tableHeaderCell: {
      padding: theme.spacing(0.5, 1.5),
      textAlign: 'left',
    },

    tableHeaderCellCheckbox: {
      padding: theme.spacing(0.5),
      textAlign: 'center',
      maxWidth: '83px',
      width: '83px',
    },
    noTenantsMessage: {
      padding: theme.spacing(2),
      textAlign: 'center',
    },
    tenantLink: {
      color: theme.palette.primary.main,
      textDecoration: 'none',
      '&:hover': {
        textDecoration: 'underline',
      },
    },
  }),
);

interface ReportingRowProps {
  tenant: HierarchyTenant;
  isReportingTenant: boolean;
  onReportingChange: (tenantId: string) => void;
}

const ReportingRow: React.FC<ReportingRowProps> = ({
  tenant,
  isReportingTenant,
  onReportingChange,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  return (
    <tr>
      <td className={classes.tableCellLeft}>
        <Typography variant="body2">
          <Link
            href={`/tenants/${tenant.id}`}
            target="_blank"
            rel="noopener noreferrer"
            className={classes.tenantLink}
          >
            {tenant.id}
          </Link>
        </Typography>
      </td>
      <td className={classes.tableCell}>
        <Typography variant="body2">{tenant.tenantType || t('tenantInformation.unset')}</Typography>
      </td>
      <td className={classes.tableCell}>
        <input
          type="radio"
          name="reporting-tenant"
          checked={isReportingTenant}
          onChange={() => onReportingChange(tenant.id)}
          aria-label={t('dataSharing.reportingAriaLabel', { tenantId: tenant.id })}
        />
      </td>
    </tr>
  );
};

interface NoReportingRowProps {
  isSelected: boolean;
  onSelect: () => void;
}

const NoReportingRow: React.FC<NoReportingRowProps> = ({ isSelected, onSelect }) => {
  const classes = useStyles();
  const { t } = useTranslation();

  return (
    <tr>
      <td className={classes.tableCellLeft} colSpan={2}>
        <Typography variant="body2">{t('dataSharing.noReporting')}</Typography>
      </td>
      <td className={classes.tableCell}>
        <input
          type="radio"
          name="reporting-tenant"
          checked={isSelected}
          onChange={onSelect}
          aria-label={t('dataSharing.noReportingAriaLabel')}
        />
      </td>
    </tr>
  );
};

/**
 * Custom RJSF widget for selecting a reporting tenant.
 * This widget displays only the tenants that have been selected for data sharing,
 * allowing the user to select one for reporting via radio buttons.
 * It receives tenant hierarchy data and selected linked tenants from DataSharingContext.
 */
export const ReportingTenantWidget: React.FC<WidgetProps> = ({ id, value, onChange, required }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const {
    hierarchyTenants = [],
    selectedLinkedTenants = [],
    initialReportingTenant,
    loading,
    fetchHierarchyData,
  } = useDataSharingContext();

  // Use empty array as default if value is undefined/null
  const reportingTenants: string[] = value ?? [];
  const reportingTenant = reportingTenants.length > 0 ? reportingTenants[0] : undefined;
  const [hasInitialized, setHasInitialized] = React.useState(false);

  // Fetch hierarchy data on mount
  React.useEffect(() => {
    fetchHierarchyData();
  }, [fetchHierarchyData]);

  // Initialize with data from API if no value is set
  // Wait for selectedLinkedTenants to be populated before initializing
  React.useEffect(() => {
    if (
      !loading &&
      !hasInitialized &&
      initialReportingTenant &&
      selectedLinkedTenants.length > 0 &&
      selectedLinkedTenants.includes(initialReportingTenant)
    ) {
      onChange([initialReportingTenant]);
      setHasInitialized(true);
    }
  }, [loading, hasInitialized, initialReportingTenant, onChange, selectedLinkedTenants]);

  //clear the selected value if it is not present in the linked tenants anymore
  React.useEffect(() => {
    if (reportingTenant && !selectedLinkedTenants.includes(reportingTenant)) {
      onChange([]);
    }
  }, [onChange, reportingTenant, selectedLinkedTenants, hasInitialized]);

  // Get the full tenant objects for the linked tenants
  const linkedTenantObjects: HierarchyTenant[] = React.useMemo(() => {
    if (!hierarchyTenants) return [];

    return hierarchyTenants.filter(
      (tenant: HierarchyTenant) =>
        selectedLinkedTenants.includes(tenant.id) && isLearnOrUnsetType(tenant.tenantType),
    );
  }, [hierarchyTenants, selectedLinkedTenants]);

  const handleReportingChange = (childId: string) => {
    onChange([childId]);
  };

  const handleNoReportingSelect = () => {
    onChange([]);
  };

  const isNoReportingSelected = !reportingTenant || reportingTenants.length === 0;

  const renderContent = () => {
    if (loading) {
      return (
        <Typography variant="body2" color="textSecondary">
          {t('dataSharing.loadingTenants')}
        </Typography>
      );
    }

    return (
      <table className={classes.table}>
        <thead>
          <tr className={classes.tableHeaderRow}>
            <th className={classes.tableHeaderCell}>
              <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                {t('dataSharing.tenantId')}
              </Typography>
            </th>
            <th className={classes.tableCellType}>
              <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                {t('dataSharing.type')}
              </Typography>
            </th>
            <th className={classes.tableHeaderCellCheckbox}>
              <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                {t('dataSharing.useInReporting')}
              </Typography>
            </th>
          </tr>
        </thead>
        <tbody>
          <NoReportingRow isSelected={isNoReportingSelected} onSelect={handleNoReportingSelect} />
          {linkedTenantObjects.map((tenant) => (
            <ReportingRow
              key={tenant.id}
              tenant={tenant}
              isReportingTenant={reportingTenant === tenant.id}
              onReportingChange={handleReportingChange}
            />
          ))}
        </tbody>
      </table>
    );
  };

  return (
    <div className={classes.container} id={id}>
      <Typography variant="h6" className={classes.label}>
        {required && <span className={classes.requiredAsterisk}>* </span>}
        {t('dataSharing.reportingTenant')}
      </Typography>
      {renderContent()}
    </div>
  );
};
